import { useCallback } from 'react';

import { useDismiss, useFloating, useInteractions, useRole } from '@floating-ui/react';
import { autoUpdate } from '@floating-ui/react-dom';

import { LAYOUTS, MIDDLEWARE } from 'bundles/ai-coach-platform/components/patterns/popover/constants';
import { useLayout } from 'bundles/ai-coach-platform/components/patterns/popover/hooks/useLayout';
import type { PopoverOptions } from 'bundles/ai-coach-platform/components/patterns/popover/types';

// Disable this feature because it requires 2 button clicks to close the popover when
// the RichTextInput has focus. Instead we use an MuiClickAwayListener to close the popover.
const ENABLE_OUTSIDE_PRESS = false;

export const usePopover = ({ open, position, onOpenChange }: PopoverOptions) => {
  const layout = useLayout();
  const dropdownEnabled = layout === LAYOUTS.DROPDOWN;

  const {
    context,
    refs: { setReference, setFloating },
  } = useFloating({
    placement: position,
    strategy: 'fixed',
    open,
    onOpenChange: dropdownEnabled ? onOpenChange : undefined,
    middleware: MIDDLEWARE,
    whileElementsMounted: autoUpdate,
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useDismiss(context, {
      enabled: dropdownEnabled,
      ancestorScroll: true,
      outsidePress: ENABLE_OUTSIDE_PRESS,
    }),
    useRole(context, { enabled: dropdownEnabled, role: 'dialog' }),
  ]);

  const toggleOpen = useCallback<() => void>(() => onOpenChange((state) => !state), [onOpenChange]);

  const referenceProps = getReferenceProps({ ref: setReference, onClick: toggleOpen });
  const floatingProps = getFloatingProps({ ref: setFloating });

  return {
    context,
    layout,
    referenceProps,
    floatingProps,
  };
};
